import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaTimes, FaSearch, FaCheckCircle } from 'react-icons/fa';
import { SupportedChainId, CHAIN_TO_CHAIN_NAME } from '../../config/cctp/chains';
import { CHAIN_IMAGES } from '../../config/chainImages';
import BridgeApiService from '../../services/bridgeApi';
import './ChainSelectModal.css';

function ChainSelectModal({ isOpen, onClose, onSelectChain, modalType, currentChain, oppositeChain, userAddress }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [chainBalances, setChainBalances] = useState({});
  const [loadingBalances, setLoadingBalances] = useState(true);

  const availableChains = Object.values(SupportedChainId);

  // Fetch USDC balances for all chains
  useEffect(() => {
    if (!isOpen || !userAddress) {
      setLoadingBalances(false);
      return;
    }

    const fetchBalances = async () => {
      setLoadingBalances(true);
      
      const result = await BridgeApiService.getUserBalances(userAddress);
      
      if (result.success) {
        setChainBalances(result.data);
      } else {
        // Set all balances to 0 on error
        const emptyBalances = {};
        availableChains.forEach(chainId => {
          emptyBalances[chainId] = '0';
        });
        setChainBalances(emptyBalances);
      }
      
      setLoadingBalances(false);
    };

    fetchBalances();
  }, [isOpen, userAddress]);

  // Filter and sort chains based on search query and balance
  const filteredChains = availableChains
    .filter((chainId) => {
      const chainName = CHAIN_TO_CHAIN_NAME[chainId];
      return chainName.toLowerCase().includes(searchQuery.toLowerCase());
    })
    .sort((a, b) => {
      // Sort by balance (highest first)
      const balanceA = parseFloat(chainBalances[a] || '0');
      const balanceB = parseFloat(chainBalances[b] || '0');
      return balanceB - balanceA;
    });

  const handleSelectChain = (chainId) => {
    onSelectChain(chainId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="chain-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="chain-modal"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chain-modal-header">
          <h3>Select {modalType === 'source' ? 'Source' : 'Destination'} Chain</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="chain-search-section">
          <div className="chain-search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search chain name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="chain-search-input"
            />
          </div>
        </div>

        <div className="chain-list">
          {filteredChains.map((chainId) => {
            const isOppositeChain = chainId === oppositeChain;
            const isSelected = chainId === currentChain;
            const balance = chainBalances[chainId] || '0';
            const formattedBalance = parseFloat(balance).toFixed(2);

            return (
              <motion.div
                key={chainId}
                className={`chain-list-item ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelectChain(chainId)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="chain-info-left">
                  <img 
                    src={CHAIN_IMAGES[chainId]} 
                    alt={CHAIN_TO_CHAIN_NAME[chainId]} 
                    className="chain-logo"
                  />
                  <div className="chain-details">
                    <span className="chain-name">{CHAIN_TO_CHAIN_NAME[chainId]}</span>
                    <span className="chain-type">USDC</span>
                  </div>
                </div>
                
                <div className="chain-info-right">
                  {isSelected && (
                    <FaCheckCircle className="selected-icon" />
                  )}
                  {isOppositeChain && (
                    <span className="swap-badge">
                      {modalType === 'source' ? 'Current Dest' : 'Current Source'}
                    </span>
                  )}
                  {userAddress && (
                    <div className="chain-balance">
                      {loadingBalances ? (
                        <span className="balance-loading">...</span>
                      ) : (
                        <>
                          <span className="balance-amount">{formattedBalance}</span>
                          <span className="balance-symbol">USDC</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default ChainSelectModal;